<!-- src/pages/HomeView.vue -->
<template>
    <div class="home-layout">
        <div class="homepage_first_hero">
            <AppHeader />
            <div class="search-hero">
                <div class="search-container">
                    <h1 class="search-title">
                        <img :src="logoPath" alt="Bass Motors" class="search-title-img" />
                    </h1>
                    <form class="search-form" @submit.prevent="handleSearch">
                        <div class="form-row">
                            <div class="form-group">
                                <select id="search-brand" v-model="searchFilters.make" class="form-control">
                                    <option value="">{{ $t('all_brands') }}</option>
                                    <option v-for="brand in uniqueBrands" :key="brand" :value="brand">{{ brand }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <select id="search-price" v-model="searchFilters.priceRange" class="form-control">
                                    <option value="">{{ $t('any_price') }}</option>
                                    <option value="0-5000">{{ $t('price_under_5000') }}</option>
                                    <option value="5000-10000">{{ $t('price_5000_10000') }}</option>
                                    <option value="10000-20000">{{ $t('price_10000_20000') }}</option>
                                    <option value="20000-50000">{{ $t('price_20000_50000') }}</option>
                                    <option value="50000-100000">{{ $t('price_50000_100000') }}</option>
                                    <option value="100000-">{{ $t('price_over_100000') }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <select id="search-fuel" v-model="searchFilters.fuelType" class="form-control">
                                    <option value="">{{ $t('any_fuel_type') }}</option>
                                    <option value="Gasoline">{{ $t('fuel_types.gasoline') }}</option>
                                    <option value="Diesel">{{ $t('fuel_types.diesel') }}</option>
                                    <option value="Electric">{{ $t('fuel_types.electric') }}</option>
                                    <option value="Electric/Gasoline">{{ $t('fuel_types.electric') }}/{{ $t('fuel_types.gasoline') }}</option>
                                    <option value="Electric/Diesel">{{ $t('fuel_types.electric') }}/{{ $t('fuel_types.diesel') }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <button type="submit" class="search-button">
                                    <span>{{ $t('search') }}</span><i class='bx bx-search-alt-2'></i>
                                </button>
                            </div>
                        </div>
                    </form>
                    <router-link :to="{ name: 'cars', params: { lang: route.params.lang || 'nl' } }" class="see-all-button">{{ $t('see_all_vehicles') }}</router-link>
                </div>
            </div>
        </div>

        <section class="latest-section">
            <div class="container">
                <h2 class="section-title">{{ $t('latest_cars') }}</h2>
                <div class="cars-grid">
                    <div v-for="car in latestCars" :key="car.id" class="car-card">
                        <router-link :to="{ name: 'car-detail', params: { lang: route.params.lang || 'nl', id: car.id } }" class="car-link">
                            <div class="car-image-wrapper">
                                <img :src="car.images[0] || 'https://via.placeholder.com/300x200'" :alt="car.make + ' ' + car.model" class="car-image">
                            </div>
                            <div class="car-info">
                                <h3 class="car-title">{{ car.make }} {{ car.model }}</h3>
                                <p class="car-price">{{ $n(car.price, 'currency') }}</p>
                                <div class="car-meta">
                                    <span class="car-year">{{ car.year }}</span>
                                    <span class="car-mileage">{{ $n(car.mileage, 'number') }} {{ $t('kilometers') }}</span>
                                </div>
                            </div>
                        </router-link>
                    </div>
                </div>

                <h2 class="section-title">{{ $t('customer_reviews') }}</h2>
               <!-- <div class="reviews-grid">
                    <div v-for="review in featuredReviews" :key="review.id" class="review-card">
                        <div class="review-header">
                            <div class="reviewer-info">
                                <div class="reviewer-avatar">
                                    {{ review.userName.charAt(0).toUpperCase() }}
                                </div>
                                <div>
                                    <h4 class="reviewer-name">{{ review.userName }}</h4>
                                    <div class="review-rating">
                                        <i v-for="star in 5" :key="star" 
                                           class='bx bx-star' 
                                           :class="{ 'filled': star <= review.rating }"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="review-car">{{ review.carMake }} {{ review.carModel }}</div>
                        </div>
                        <p class="review-content">{{ review.comment[$i18n.locale] }}</p>
                        <div class="review-date">{{ formatDate(review.date) }}</div>
                    </div>
                </div> -->
		<iframe src='https://widgets.sociablekit.com/google-reviews/iframe/25561067' 
	width='100%' 
	height='500'
	style="border:0;" 
	allowfullscreen="" 
	loading="eager" 
	></iframe>
            </div>
        </section>

        <AppFooter />
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue'
    import { useRouter, useRoute } from 'vue-router'
    import { useCarsStore } from '@/stores/cars'
    import AppHeader from '@/components/AppHeader.vue'
    import AppFooter from '@/components/AppFooter.vue'
    import { useI18n } from 'vue-i18n'
    import logoNL from '@/assets/images/bm_NL_white.png'
    import logoFR from '@/assets/images/bm_FR_white.png'
    import logoEN from '@/assets/images/bm_EN_white.png'

    const { locale } = useI18n()
    const logoPath = computed(() => {
        const path = {
            nl: logoNL,
            fr: logoFR,
            en: logoEN
        }[locale.value] || logoEN

        return path
    })

    const router = useRouter()
    const route = useRoute()
    const carsStore = useCarsStore()
    const { updateFilters, fetchCars } = carsStore
    const searchFilters = ref({ make: '', priceRange: '', fuelType: '' })

    onMounted(async () => {
        await fetchCars()
    })

    const uniqueBrands = computed(() => {
        const brands = new Set()
        carsStore.cars.forEach(car => brands.add(car.make))
        return Array.from(brands).sort()
    })

    const latestCars = computed(() => {
        return [...carsStore.cars]
            .sort((a, b) => new Date(b.publication_date) - new Date(a.publication_date))
            .slice(0, 4)
    })

    const handleSearch = () => {
        let minPrice = null
        let maxPrice = null
        if (searchFilters.value.priceRange) {
            const [min, max] = searchFilters.value.priceRange.split('-')
            minPrice = min ? parseInt(min) : null
            maxPrice = max ? parseInt(max) : null
        }
        updateFilters({
            make: searchFilters.value.make,
            minPrice,
            maxPrice,
            fuelType: searchFilters.value.fuelType
        })
        router.push({ name: 'cars', params: { lang: route.params.lang || 'nl' } })
    }
</script>

<style scoped>
    .home-layout {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        background-size: cover;
    }

    .homepage_first_hero {
        background: url('../assets/images/hero_img.jpg') no-repeat center;
        background-size: cover;
    }

    .search-hero {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0 2rem;
        min-height: 80vh;
    }

    .search-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    /* .search-title {
        color: white;
        font-size: 80px;
        font-weight: 600;
        margin-bottom: 2rem;
        text-align: center;
    } */
    .search-title-img {
        width: 900px; /* adjust as needed */
        max-width: 90%;
        height: auto;
        display: block;
        margin: 0 auto;
        margin-bottom: 30px;
    }

    .search-form {
        background: #fff;
        border-radius: 20px;
        padding: 16px 22px 17px 22px;
        box-shadow: 0 12px 0px 0px rgb(197 197 197 / 70%);
        margin-bottom: 2rem;
    }

    .form-row {
        display: flex;
        gap: 1rem;
        align-items: flex-end;
    }

    .form-control {
        border: 1px solid #e7edf3;
        box-shadow: 1px 1px 0 0 rgba(196, 196, 196, 0.24);
        padding: 0 0 0 20px;
        background: #fff;
        border-radius: 10px;
        position: relative;
        height: 50px;
        width: 160px;
    }

    .search-button {
        overflow: hidden;
        display: inline-block;
        font-size: 17px;
        line-height: 21px;
        font-weight: 600;
        text-align: center;
        color: #fff;
        border: 0 solid transparent;
        box-shadow: none;
        cursor: pointer;
        padding: 5px 10px 5px 10px;
        vertical-align: top;
        border-radius: 10px;
        background-color: #45ABFF;
        transition: all 0.2s ease-in-out;
        font-size: 35px;
        height: 50px;
    }

    .search-button:hover {
        background-color: #3a96e0;
    }

    .search-button span {
        display: none;
    }

    .see-all-button {
        padding: 15px 50px;
        background-color: #45ABFF;
        color: #FFFFFF;
        border: none;
        border-radius: 10px;
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        text-decoration: none;
    }

    .see-all-button:hover {
        opacity: 0.7;
    }

    .latest-section {
        padding: 4rem 2rem;
        background-color: #f8f9fa;
    }

    .container {
        max-width: 1200px;
        margin: 0 auto;
    }

    .section-title {
        text-align: center;
        margin-bottom: 2rem;
        font-size: 2rem;
        color: #333;
        position: relative;
        padding-bottom: 0.5rem;
    }

    .section-title::after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 80px;
        height: 3px;
        background-color: #45ABFF;
        border-radius: 80px;
    }

    .cars-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 2rem;
        margin-bottom: 4rem;
    }

    .car-card {
        background: white;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        transition: transform 0.3s ease;
    }

    .car-card:hover {
        transform: translateY(-5px);
    }

    .car-link {
        text-decoration: none;
        color: inherit;
    }

    .car-image-wrapper {
        height: 200px;
        overflow: hidden;
    }

    .car-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
    }

    .car-card:hover .car-image {
        transform: scale(1.05);
    }

    .car-info {
        padding: 1.5rem;
    }

    .car-title {
        margin: 0 0 0.5rem;
        font-size: 1.25rem;
        color: #333;
    }

    .car-price {
        margin: 0 0 0.5rem;
        font-size: 1.1rem;
        font-weight: bold;
        color: #45ABFF;
    }

    .car-meta {
        display: flex;
        gap: 1rem;
        color: #666;
        font-size: 0.9rem;
    }

    .reviews-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 2rem;
    }

    .review-card {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background: white;
        border-radius: 8px;
        padding: 1.5rem;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .review-header {
        display: flex;
        flex-direction: column;
        margin-bottom: 1rem;
    }

    .reviewer-info {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 0.5rem;
    }

    .reviewer-avatar {
        width: 40px;
        height: 40px;
        background-color: #45ABFF;
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
    }

    .reviewer-name {
        margin: 0;
        font-size: 1rem;
    }

    .review-rating {
        color: #FFD700;
        font-size: 1rem;
    }

    .review-rating .bx-star {
        color: #ddd;
    }

    .review-rating .bx-star.filled {
        color: #FFD700;
    }

    .review-car {
        font-size: 0.9rem;
        color: #666;
    }

    .review-content {
        color: #333;
        line-height: 1.6;
        margin-bottom: 1rem;
    }

    .review-date {
        font-size: 0.8rem;
        color: #999;
        text-align: right;
    }

    @media (max-width: 768px) {
        .search-title {
            /* font-size: 2.5rem; */
            margin-top: 50px;
        }

        .form-row {
            flex-direction: column;
        }

        .form-control {
            width: 100%;
        }

        .search-form, .form-group {
            width: 100%;
        }

        .search-button {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
        }

        .search-button span {
            font-size: 18px;
            display: inline;
        }

        .search-button i {
            font-size: 28px;
        }

        .section-title {
            font-size: 1.5rem;
        }

        .cars-grid, .reviews-grid {
            grid-template-columns: 1fr;
        }
    }
</style>
